<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-between tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">{{ pageTitle }}</h1>
        <div v-if="hideCreation === false">
          <base-dropdown
            :options="['Create Plan', 'Send Invite']"
            :showBackground="false"
            color="primary"
            type="primary"
            v-if="organization.operatingModel === 'coordinator'"
            align="right"
          >
            <base-button color="primary" type="primary">
              <div class="tw-flex tw-items-center tw-cursor-pointer">
                <div>{{ newMemberButtonTitle }}</div>
                <chevron-down-icon class="tw-h-5 tw-w-5 tw-ml-2" />
              </div>
            </base-button>

            <template #option="{ option: link }">
              <button
                :disabled="!organization.isActive"
                class="tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 focus:tw-outline-none"
                @click="link === 'Create Plan' ? createMemberPlan() : inviteMemberPlan()"
              >
                {{ link }}
              </button>
            </template>
          </base-dropdown>
          <base-button v-else type="primary" color="primary" @click="createMemberPlan()" :disabled="!organization.isActive">
            {{ newMemberButtonTitle }}
          </base-button>
        </div>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" ref="searchInput" class="w-full tw-mb-6" :placeholder="searchPlaceholder" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-2">
        <template #avatar="{item: member}">
          <div class="tw-h-12 tw-w-12">
            <Avatar :userId="member.userId" avatarType="user" />
          </div>
        </template>
        <template #member="{item: member}">
          <div class="tw-flex">
            <base-button type="text" size="lg" @click="view(member.userId)">
              <span class="tw-whitespace-nowrap tw-text-left hover:tw-underline">{{ member.fullName }}</span>
            </base-button>
            <base-badge class="tw-ml-3 tw-my-auto" size="sm" :color="badgeColor(member.status)"> {{ member.status }}</base-badge>
          </div>
          <div class="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ member.email }}</div>
        </template>
        <template #phone="{item: member}">
          <base-phone-number-field :value="member.phone" />
        </template>
        <template #dob="{item: member}">
          <div class="tw-whitespace-nowrap">{{ formatDate(member.dob) }}</div>
        </template>
        <template #state="{item: member}">
          <div class="tw-whitespace-nowrap">{{ member.state }}</div>
        </template>
        <template #location="{item: member}">
          <div class="tw-flex">
            <div>
              <div class="tw-whitespace-nowrap">
                {{ lookupLocations(member.locations) }}
              </div>
            </div>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-3 tw-w-40" />
              <base-skeleton-loader class="tw-h-3 tw-w-12 tw-rounded-full tw-ml-3" />
            </div>
            <base-skeleton-loader class="tw-h-2 tw-w-24 tw-mt-2" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4" v-if="organization.collectDobSetting">
            <base-skeleton-loader class="tw-h-3 tw-w-20" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4" v-if="organization.useCreditsSetting" align="right">
            <base-skeleton-loader class="tw-h-3 tw-w-12" />
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <create-member-plan-slide-over ref="createMemberSlideOver" :business-id="businessId" />
    <invite-member-plan-slide-over ref="inviteMemberSlideOver" :business-id="businessId" @invited="loadPage" />
  </div>
</template>
<script>
import CreateMemberPlanSlideOver from "@/views/business/member-search/components/CreateMemberPlanSlideOver";
import InviteMemberPlanSlideOver from "@/views/business/member-search/components/InviteMemberPlanSlideOver";
import Avatar from "@/components/Avatar";
import { SearchIcon, ChevronDownIcon } from "@/assets/svgs";
import { debounce } from "debounce";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize, uniq } from "lodash";

const TAKE_KEY = "predictile_business_members_take";

export default {
  name: "BusinessMembersView",
  components: {
    CreateMemberPlanSlideOver,
    InviteMemberPlanSlideOver,
    SearchIcon,
    ChevronDownIcon,
    Avatar,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      query: null,
      isLoading: false,
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["locations"]),
    ...mapGetters("role", ["isLocationFiltered"]),
    hideCreation() {
      return this.isLocationFiltered && this.organization.usePrescriptionProgramSetting;
    },
    fields() {
      let columns = [];

      columns.push({ title: "", key: "avatar", thClass: "tw-w-0 tw-text-center" });
      columns.push({ title: this.organization.customerLabelSingular, key: "member" });
      columns.push({ title: "Phone", key: "phone" });

      if (this.organization.collectDobSetting) columns.push({ title: "DOB", key: "dob" });
      columns.push({ title: "State", key: "state" });

      columns.push({ title: "Location", key: "location" });

      if (this.organization.useCreditsSetting === true) columns.push({ title: "Credits", key: "creditBalance", align: "right", thClass: "tw-text-right" });

      return columns;
    },
    pageTitle() {
      return `${capitalize(this.organization.customerLabelPlural)}`;
    },
    newMemberButtonTitle() {
      return `New ${capitalize(this.organization.customerLabelSingular)}`;
    },
    searchPlaceholder() {
      return `Search for a ${this.organization.customerLabelSingular}`;
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    this.$refs.searchInput.focus();
  },
  methods: {
    badgeColor(status) {
      let color;
      switch (status) {
        case "Active":
          color = "green";
          break;
        case "Failed Payment":
          color = "red";
          break;
        case "Paused":
        case "Cancelled":
        case "Awaiting Payment":
        case "Deleted":
        case "New Rx":
        case "Rx Contacted":
        case "Rx Declined":
        case "Rx Accepted":
        case "Rx Changed":
          color = "gray";
          break;
        default:
          color = "gray";
      }
      return color;
    },
    lookupLocations(input) {
      return uniq(
        input
          .split(",")
          .map((l) => {
            const location = this.locations.find((loc) => loc.businessLocationId === l);
            return location ? location.name : null;
          })
          .filter((location) => location),
      ).join(", ");
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    view(userId) {
      if (this.organization.usePrescriptionProgramSetting) {
        this.$router.push(`/businesses/${this.businessId}/members/${userId}/prescriptions`);
      } else {
        this.$router.push(`/businesses/${this.businessId}/members/${userId}/plans`);
      }
    },
    loadPage() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/members/?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.isLoading = false;
          this.items = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load ${this.organization.customerLabelPlural}`,
            message: `We had a problem retrieving the list of ${this.organization.customerLabelPlural}. Please reload the page to try again.`,
            error: err,
          });
        });
    },
    createMemberPlan() {
      this.$refs.createMemberSlideOver.show();
    },
    inviteMemberPlan() {
      this.$refs.inviteMemberSlideOver.show();
    },
    formatDate(date) {
      if (!date) return "-";
      return new moment.utc(date).local().format("MM/DD/YYYY");
    },
  },
};
</script>
